<script setup lang="ts">
import type { Upload, Track } from '~/types'

import { reactive, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import AttachmentInput from '~/components/common/AttachmentInput.vue'

import Input from '~/ui/Input.vue'
import Tokens from '~/ui/Tokens.vue'
import { useDataStore } from '~/data'

// TODO: Move writable types into `~/data`
// Dirty quickfix: `tags` is made writable here
type Values
  = Pick<Track, 'title' | 'position' | 'tags'>
    & { cover: string | null, description: string }
    & { -readonly [P in 'tags']: Track['tags'] }

// TODO: Use mod
interface Events {
  (e: 'update:values', values: Values): void
}

interface Props {
  upload: Upload
  values?: Partial<Values> | null
}

const props = withDefaults(defineProps<Props>(), {
  values: null
})

const emit = defineEmits<Events>()

const { t } = useI18n()

// Initialize new values
// TODO: check if `position: 0` is a good default
const newValues = reactive<Values>({
  position: 0,
  description: '',
  title: '',
  tags: props.values?.tags ?? [],
  cover: null,
  ...(props.values ?? props.upload.import_metadata)
})

const isLoading = computed(() => !props.upload)
watch(newValues, values => emit('update:values', values), { immediate: true })
</script>

<template>
  <form
    class="layout-stack"
    ui-gap="32"
  >
    <Loader v-if="isLoading" />
    <Input
      v-model="newValues.title"
      :label="t('components.channels.UploadMetadataForm.label.title')"
      required
    />
    <AttachmentInput
      v-model="newValues.cover"
      @delete="newValues.cover = ''"
    >
      {{ t('components.channels.UploadMetadataForm.label.image') }}
    </AttachmentInput>
    <Tokens
      :model-value="newValues.tags.map(tag => ({ label: tag, isCustom: false }))"
      :label="t('components.channels.UploadMetadataForm.label.tags')"
      prefix="#"
      :suggestions="useDataStore().allTags({}).value.data?.results?.map(({ name }) => ({ label: name, isCustom: false }))"
      @update:model-value="newTokens => newValues.tags = newTokens.map(token => token.label)"
    />
    <Spacer />
    <Input
      v-model="newValues.position"
      type="number"
      min="1"
      step="1"
      :label="t('components.channels.UploadMetadataForm.label.position')"
    />
    <label for="upload-description">
      {{ t('components.channels.UploadMetadataForm.label.description') }}
    </label>
    <content-form
      v-model="newValues.description"
      field-id="upload-description"
    />
  </form>
</template>
